import React from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, FieldArray, reduxForm } from 'redux-form';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import RenderTextField from '../../../utils/renderFields/RenderTextField';
import RenderCheckboxField from '../../../utils/renderFields/RenderCheckboxField';
import validate from '../validate';
import countries from '../../ImportCommands/utils/list/countryCode';
import { replaceBufferQuantity, updateItem } from '../stockActions';
import RenderStockItem from './drawer/RenderStockItem';
import RenderSelectField from '../../../utils/renderFields/RenderSelectField';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin: 50px 0px;
`;

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3)
  }
}));

const objectToId = values =>
  values.requiredAttributes.filter(x => x.isPresent !== false).map(a => a.id);

const isPresent = (values, requiredAttributes) => {
  values.forEach(element => {
    // eslint-disable-next-line no-param-reassign
    element.isPresent = !!requiredAttributes.find(x => x.id === element.id);
  });
  return values;
};

let StockItem = ({ handleSubmit, requiredAttributes, initialValues }) => {
  const classes = useStyles();

  return (
    <form className={classes.form} onSubmit={handleSubmit} noValidate>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="name"
            id="name"
            label="Nom"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="barcodeValue"
            id="barcodeValue"
            label="Code-barres"
            fullWidth
            disabled
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="forecastQuantity"
            id="forecastQuantity"
            label="Quantité Prévisionnelle"
            type="number"
            fullWidth
            displayErrorBeforeSubmit
            disabled
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="quantity"
            id="quantity"
            label="Quantité"
            type="number"
            fullWidth
            displayErrorBeforeSubmit
            disabled
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="bufferQuantity"
            id="bufferQuantity"
            label="Buffer"
            type="number"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="defectiveQuantity"
            id="defectiveQuantity"
            label="Défectueux"
            fullWidth
            disabled
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="size"
            id="size"
            label="Taille"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="color"
            id="color"
            label="Couleur"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="value"
            id="value"
            label="Valeur"
            type="number"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            name="material"
            id="material"
            label="Matériel"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
      </Grid>
      <Grid container justify="center" spacing={7}>
        <Grid item md={6}>
          <Field
            name="reference"
            id="reference"
            label="Référence"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderTextField}
          />
        </Grid>
        <Grid item md={6}>
          <Field
            style={{ minHeight: 50 }}
            name="countryOfOrigin"
            id="countryOfOrigin"
            label="Fabriqué en"
            datas={countries}
            fullWidth
            displayErrorBeforeSubmit
            component={RenderSelectField}
          />
        </Grid>
      </Grid>
      {initialValues.batchesQuantities.length > 0 && (
        <Box
          marginTop={1}
          padding={1}
          style={{ border: 'solid', borderWidth: 0.2 }}
        >
          <>
            <Typography style={{ fontWeight: 'bold' }}>
              Quantité par lot :
            </Typography>
            {initialValues.batchesQuantities.map(batchQuantity => (
              <Typography>
                {`${batchQuantity.reference} ${
                  batchQuantity.expirationDate
                    ? `${batchQuantity.expirationDate} `
                    : ''
                }:
                      ${batchQuantity.quantity}
                    `}
              </Typography>
            ))}
            <Typography style={{ fontWeight: '500' }}>
              {`Total : ${initialValues.quantity}`}
            </Typography>
          </>
        </Box>
      )}
      <Grid container justify="center" spacing={7}>
        <Grid item md={12}>
          <Field
            name="insuranceRequired"
            id="insuranceRequired"
            label="Assurer les produits à la valeur indiquée"
            fullWidth
            displayErrorBeforeSubmit
            component={RenderCheckboxField}
          />
        </Grid>
      </Grid>
      <h2>Les attributs requis</h2>
      <FieldArray
        name="requiredAttributes"
        component={RenderStockItem}
        requiredAttributes={initialValues.requiredAttributes}
        currentStockItem={requiredAttributes}
      />
      <ButtonContainer>
        <Button variant="contained" color="secondary" type="submit">
          Modifier cet objet
        </Button>
      </ButtonContainer>
    </form>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchUpdateItem: item => dispatch(updateItem(item))
});

const mapStateToProps = state => {
  const {
    batchesQuantities,
    id,
    name,
    color,
    size,
    material,
    value,
    barcodeValue,
    reference,
    forecastQuantity,
    quantity,
    bufferQuantity,
    defectiveQuantity,
    countryOfOrigin
  } = state.stock.currentItem;
  return {
    id,
    requiredAttributes: state.stock.currentItem.requiredAttributes,
    initialValues: {
      batchesQuantities,
      name,
      color,
      size,
      material,
      value,
      barcodeValue,
      reference,
      forecastQuantity,
      quantity,
      bufferQuantity,
      defectiveQuantity,
      countryOfOrigin,
      insuranceRequired: state.stock.currentItem.isInsuranceRequired,
      requiredAttributes: isPresent(
        state.stock.requiredAttributes,
        state.stock.currentItem.requiredAttributes
      )
    }
  };
};

StockItem = reduxForm({
  form: 'updateStockItemForm',
  validate,
  onSubmit: (values, dispatch, props) => {
    let onSuccessCallback = null;
    if (props.initialValues.bufferQuantity !== values.bufferQuantity) {
      onSuccessCallback = () => {
        const bufferQuantityData = {
          currentExpectedBufferQuantity: props.initialValues.bufferQuantity,
          bufferQuantity: values.bufferQuantity,
          id: props.id
        };
        dispatch(replaceBufferQuantity(bufferQuantityData));
      };
    }
    const stockItem = {
      ...values,
      requiredAttributes: objectToId(values),
      quantity: +values.quantity,
      id: props.id,
      onSuccess: onSuccessCallback
    };
    dispatch(updateItem(stockItem));
  }
})(StockItem);

StockItem.propTypes = {
  handleSubmit: PropTypes.func,
  requiredAttributes: PropTypes.array.isRequired,
  initialValues: PropTypes.object
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(StockItem)
);
